import { ClientRepo } from "@services/clients/client.repo";
import { PortfolioRepo } from "@services/portfolio.repo";
import { TradingRepo } from "@services/trading.repo";
import { CModelRepo } from "@services/cmodel.repo";
import { TradePortfolio } from "@models/trading/tradePortfolio";
import { TradeRestriction } from "@models/trading/tradeRestriction";
import { AssetClassNameArray } from "@common/enums/AssetClassType";
import * as moment from "moment";
import * as _ from "lodash";
import angular = require("angular");
import { ClientDetails } from "@models/clients/clientDetailsModel";
import { DropdownOptionsService } from "@services/clients/dropdownOptions.service";
import { ClientDetailsService } from "@services/clients/clients.details/clientdetails.service";
import { ClientCRMRepo } from "@services/clients/clientCRM.repo";
import { Restriction } from "@models/clients/clients.details/restriction";
import { ManagementFee } from "@models/clients/clients.details/managementFee";
import { extractErrorMessage } from "@common/utils/errorUtils";

export class ClientDetailsPortfolioController {
    public client: ClientDetails;
    public clientNumber: string; // Add clientNumber binding
    public isViewOnly: boolean = false;
    public portfolioRiskProfiles: any[] = [];
    public portfolioTradeRestrictions: TradePortfolio[] = [];
    public selectedTradePortfolio: TradePortfolio;
    public securities: Array<any> = [];
    public isLoading: boolean = false;

    // Loading states for each card
    public isLoadingRiskProfile: boolean = false;
    public isLoadingPortfolios: boolean = false;
    public isLoadingTradeRestrictions: boolean = false;
    public isLoadingSecurities: boolean = false;
    public loadingRestrictions: boolean = false;
    public loadingManagementFees: boolean = false;

    // Restrictions and Management Fees data
    public restrictions: Restriction[] = [];
    public managementFees: ManagementFee[] = [];
    public clientPortfolioAccounts: any[] = [];

    private assetClassTypes = AssetClassNameArray;

    public dropdownOptions: any = {};

    // Table column definitions
    public restrictionColumns: any[] = [
        { key: 'assetClassName', label: 'Asset Class', type: 'text' },
        { key: 'nameOfPosition', label: 'Name Of Position', type: 'text' },
        { key: 'startDate', label: 'Start Date', type: 'date' },
        { key: 'endDate', label: 'End Date', type: 'date' }
    ];

    public managementFeeColumns: any[] = [
        { key: 'portfolioNumber', label: 'Portfolio Number', type: 'text' },
        { key: 'dateWhichStartedToBeManaged', label: 'Management start date', type: 'date' },
        { key: 'dateOnWhichEnded', label: 'Management end date', type: 'date' },
        { key: 'managementFeeTypeName', label: 'Management Fee Types', type: 'text' },
        { key: 'startDateFeeType', label: 'Fee start date', type: 'date' },
        { key: 'endDateFeeType', label: 'Fee end date', type: 'date' },
        { key: 'amendmentToManagementFee', label: 'Amendment Reason', type: 'text' },
        { key: 'comments', label: 'Comments', type: 'text' }
    ];

    /** @ngInject */
    constructor(
        private clientRepo: ClientRepo,
        private portfolioRepo: PortfolioRepo,
        private tradingRepo: TradingRepo,
        private cModelRepo: CModelRepo,
        private dropdownOptionsService: DropdownOptionsService,
        private clientDetailsService: ClientDetailsService,
        private clientCRMRepo: ClientCRMRepo,
        private $rootScope: angular.IRootScopeService,
        private $scope: angular.IScope,
        private $mdDialog: angular.material.IDialogService,
        private notifyjs: any,
        private $window: angular.IWindowService
    ) {}

    public $onInit(): void {
      
      // Listen for dropdown options loaded event from parent
      this.$scope.$on('dropdownOptionsLoaded', (event: any, options: any) => {
          this.dropdownOptions = options;
      });
      
      // Try to get options immediately if they're already loaded
        this.dropdownOptionsService.getAllOptions()
          .then((options) => {
              this.dropdownOptions = options;
          })
          .catch((error) => {
              console.warn('Failed to get dropdown options immediately:', error);
          });
      
        // Use clientNumber for initial data loading instead of waiting for client
        if (this.clientNumber && this.clientNumber !== 'Individual' && this.clientNumber !== 'Institution') {
          this.setLoadingStates(true);
          this.loadPortfolioData();
        }
    }



    private setLoadingStates(isLoading: boolean): void {
        this.isLoadingRiskProfile = isLoading;
        this.isLoadingPortfolios = isLoading;
        this.isLoadingTradeRestrictions = isLoading;
        this.isLoadingSecurities = isLoading;
    }

    private loadPortfolioData(): void {
        // Check if this is a new client - don't load portfolio data for new clients
        if (!this.clientNumber || this.clientNumber === 'Individual' || this.clientNumber === 'Institution') {
            // For new clients, turn off all loading states since no data needs to be loaded
            this.setLoadingStates(false);
            return;
        }

        // Portfolio data is available directly from client, so turn off its loading immediately
        this.isLoadingPortfolios = false;

        this.getClientPortfolioRiskProfile(this.clientNumber); 
        this.getClientPortfolioRestrictions(this.clientNumber);
        this.getSecurities();
        this.loadRestrictions();
        this.loadManagementFees();
    }

    public getClientPortfolioRiskProfile = (clientNumber: string) => {

        if (!this.portfolioRepo) {
            // console.error('PortfolioRepo is not available!');
            this.notifyjs('Portfolio repository service is not available');
            return;
        }

        this.isLoadingRiskProfile = true;
        // console.log('Calling portfolioRepo.getClientPortfolioRiskProfile...');
        this.portfolioRepo.getClientPortfolioRiskProfile(clientNumber).then((res) => {
            // console.log('Risk profile response:', res);
            this.portfolioRiskProfiles = res.data as Array<any>;
            // console.log('Parsed portfolioRiskProfiles:', this.portfolioRiskProfiles);
            this.portfolioRiskProfiles.forEach((portfolioRiskProfile) => {
                portfolioRiskProfile.isEditing = false;
                this.addDefaultRiskProfile(portfolioRiskProfile);
            });
        }).catch((err) => {
            // console.error('Error loading risk profile:', err);
            this.notifyjs('Failed to load risk profile: ' + extractErrorMessage(err));
        }).finally(() => {
            this.isLoadingRiskProfile = false;
        });
    };

    public getClientPortfolioRestrictions(clientNumber: string) {
        this.isLoadingTradeRestrictions = true;
        return this.tradingRepo
            .getClientPortfolioTradeRestrictions(clientNumber)
            .then((res) => {
                let tradingPortfolios = (res.data as any[])
                    .map((tp) => new TradePortfolio(tp))
                    .sort((a, b) => (a.name > b.name ? 1 : -1));

                tradingPortfolios.forEach((tp) =>
                    tp.tradingRestrictions.sort((a, b) => {
                        let aName = a.security ? "security" : "assetClass";
                        let bName = b.security ? "security" : "assetClass";
                        return a[aName].name > b[bName].name ? 1 : -1;
                    })
                );
                this.portfolioTradeRestrictions = tradingPortfolios;
            })
            .catch((err) => this.notifyjs(extractErrorMessage(err, "Failed to load trade restrictions")))
            .finally(() => (this.isLoadingTradeRestrictions = false));
    }

    public getSecurities() {
        if (this.securities && this.securities.length > 0) {
            this.isLoadingSecurities = false;
            return;
        }

        this.isLoadingSecurities = true;
        this.cModelRepo
            .getSecurityItemsList(null, null, false)
            .then((res) => {
                this.securities = res;
                this.securities.sort((a, b) => (a.name > b.name ? 1 : -1));
            })
            .catch((err) => {
                this.notifyjs(extractErrorMessage(err, "Failed to load securities"));
            })
            .finally(() => {
                this.isLoadingSecurities = false;
            });
    }

    public addDefaultRiskProfile = (portfolio: any) => {
        if (portfolio.riskProfiles.length == 0) {
            this.assetClassTypes.forEach((assetClassName) => {
                portfolio.riskProfiles.push({
                    high: 0,
                    low: 0,
                    assetClass: {
                        name: assetClassName,
                    },
                });
            });
        } else if (portfolio.riskProfiles.length != this.assetClassTypes.length) {
            let assetClassesWithoutRiskProfiles = _.difference(
                this.assetClassTypes,
                portfolio.riskProfiles.map((profile) => profile.assetClass.name)
            );
            assetClassesWithoutRiskProfiles.forEach((assetClassName) => {
                portfolio.riskProfiles.push({
                    high: 0,
                    low: 0,
                    assetClass: {
                        name: assetClassName,
                    },
                });
            });
        }
    };

    public savePortfolioRiskProfile = (portfolioRiskProfile: any) => {
        portfolioRiskProfile.isEditing = false;
        this.portfolioRepo
            .savePortfolioRiskProfile(
                portfolioRiskProfile.portfolioId,
                portfolioRiskProfile.riskProfiles
            )
            .then((res) => {
                this.getClientPortfolioRiskProfile(this.clientNumber);
            })
            .catch((err) => {
                this.notifyjs(`Could not save portfolio risk profile : ${extractErrorMessage(err)}`);
                this.getClientPortfolioRiskProfile(this.clientNumber);
            });
    };

    public startEditingPortfolioRiskProfile = (portfolio: any) => {
        // deep copy instead of recalling the backend to get the original risk profiles
        portfolio._originalRiskProfiles = angular.copy(portfolio.riskProfiles);
        portfolio.isEditing = true;
    };

    public cancelPortfolioRiskProfile = (portfolio: any) => {
        portfolio.riskProfiles = portfolio._originalRiskProfiles;
        portfolio._originalRiskProfiles = null;
        portfolio.isEditing = false;
    };

    public newPortfolio(): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.clientNumber = this.clientNumber;
        tempScope.clientId = this.client && this.client.id ? this.client.id : null;

        // Test the new reusable modal
        this.$mdDialog
            .show({
                template:
                    '<md-dialog flex="50"><new-portfolio-modal client-number="clientNumber" client-id="clientId"></new-portfolio-modal></md-dialog>',
                scope: tempScope,
                clickOutsideToClose: true,
                escapeToClose: true
            })
            .then((result) => {
                if (result) {
                    this.portfolioSaved(result);
                }
            });
    }

    private portfolioSaved(result: any) {
        if (result) {
            const index = this.client.portfolios.findIndex(
                (current) => current.portfolioId === result.portfolioId
            );
            if (index > -1) {
                this.client.portfolios[index] = result;
            } else {
                this.client.portfolios.push(result);
            }
        }
    }

    public editPortfolio(linkedPortfolioId: number) {
        if (this.isViewOnly) return;
        // Open modal immediately - it will fetch data internally and show loader
        const tempScope = this.$rootScope.$new(true);
        tempScope["client"] = this.client;
        tempScope["portfolioId"] = linkedPortfolioId;
        tempScope["clientNumber"] = this.clientNumber;
        tempScope["clientId"] = this.client && this.client.id ? this.client.id : null;

        // Test the new reusable modal
        this.$mdDialog
            .show({
                template:
                    '<md-dialog flex="50"><new-portfolio-modal client-number="clientNumber" client-id="clientId" portfolio-id="portfolioId"></new-portfolio-modal></md-dialog>',
                scope: tempScope,
                clickOutsideToClose: true,
                escapeToClose: true
            })
            .then((result) => {
                this.portfolioSaved(result);
            });
    }

    public movePortfolio(portfolio: any) {
        if (this.isViewOnly) return;
        
        const tempScope = this.$rootScope.$new(true);
        tempScope["oldClient"] = this.client;
        tempScope["portfolio"] = portfolio;

        this.$mdDialog
            .show({
                template:
                    '<md-dialog flex="30"><portfolio-transfer old-client="oldClient" portfolio="portfolio"></portfolio-transfer></md-dialog>',
                scope: tempScope,
            })
            .then(() => {
                this.$window.location.reload();
            })
            .catch(() => {});
    }

    public newTradeRestriction(tradePortfolio: TradePortfolio) {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.tradePortfolio = tradePortfolio;
        tempScope.newTradeRestriction = true;
        tempScope.securityTypeRestriction = true;
        tempScope.tradeRestriction = {};
        tempScope.securities = this.securities;

        this.$mdDialog
            .show({
                template:
                    '<md-dialog flex="30"><edit-trade-restriction-modal client-number="clientNumber" security-Type-Restriction="securityTypeRestriction" securities="securities" trade-Restriction="tradeRestriction" trade-portfolio="tradePortfolio" new-trade-restriction="newTradeRestriction"></edit-trade-restriction-modal></md-dialog>',
                scope: tempScope,
            })
            .then((result) => {
                this.getClientPortfolioRestrictions(this.clientNumber).then(() => {
                    this.selectedTradePortfolio = this.portfolioTradeRestrictions.find(
                        (ptr) => ptr.id == this.selectedTradePortfolio.id
                    );
                });
            });
    }

    public editTradeRestriction(tradeRestriction: TradeRestriction) {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.tradePortfolio = this.selectedTradePortfolio;
        tempScope.newTradeRestriction = false;
        tempScope.securityTypeRestriction = null;
        tempScope.securities = this.securities;
        tempScope.tradeRestriction = {};
        angular.copy(tradeRestriction, tempScope.tradeRestriction);

        if (tradeRestriction.assetClass == null) {
            tempScope.securityTypeRestriction = true;
        } else {
            tempScope.securityTypeRestriction = false;
        }

        this.$mdDialog
            .show({
                template:
                    '<md-dialog flex="30"><edit-trade-restriction-modal client-number="clientNumber" security-Type-Restriction="securityTypeRestriction" securities="securities" trade-Restriction="tradeRestriction" trade-portfolio="tradePortfolio" new-trade-restriction="newTradeRestriction"></edit-trade-restriction-modal></md-dialog>',
                scope: tempScope,
            })
            .then((result) => {
                if (result != null) {
                    this.getClientPortfolioRestrictions(this.clientNumber).then(() => {
                        this.selectedTradePortfolio = this.portfolioTradeRestrictions.find(
                            (ptr) => ptr.id == this.selectedTradePortfolio.id
                        );
                        this.selectedTradePortfolio.tradingRestrictions =
                            this.selectedTradePortfolio.tradingRestrictions.filter(
                                (tr) => tr.id != tradeRestriction.id
                            );
                        this.selectedTradePortfolio.tradingRestrictions.push(
                            new TradeRestriction(result)
                        );
                        this.selectedTradePortfolio.tradingRestrictions.sort((a, b) =>
                            a.security.name > b.security.name ? 1 : -1
                        );
                    });
                }
            });
    }


  public exportDebitAccountsToExcel = () => {
    // Broadcast event down the scope tree so DebitAccountsComponent can handle export
    this.$scope.$broadcast('exportDebitAccountsExcel');
  };


  // Helper method to check if client is individual
  public isIndividualClient(): boolean {
    return this.clientDetailsService.isIndividual(this.client);
  }


  // Load restrictions
  public loadRestrictions(): void {
    if (this.clientNumber) {
      this.loadingRestrictions = true;
      this.clientCRMRepo.getRestrictions(this.clientNumber)
        .then((response: any) => {
          this.restrictions = response.data || [];
          this.loadingRestrictions = false;
        })
        .catch((error) => {
          console.error('Error loading restrictions:', error);
          this.loadingRestrictions = false;
        });
    }
  }

  // Load management fees
  public loadManagementFees(): void {
    if (this.clientNumber) {
      this.loadingManagementFees = true;
      this.clientCRMRepo.getManagementFees(this.clientNumber)
        .then((response: any) => {
          this.managementFees = response.data || [];
          this.loadingManagementFees = false;
        })
        .catch((error) => {
          console.error('Error loading management fees:', error);
          this.loadingManagementFees = false;
        });
    }
  }

    // New restriction modal
    public newRestriction(): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.restriction = {
            portfolioNumber: '',
            assetClass: undefined,
            nameOfPosition: '',
            startDate: undefined,
            endDate: undefined
        };
    
    // Pass dropdown options and portfolio accounts to the modal
    tempScope.dropdownOptions = this.dropdownOptions;
    tempScope.clientPortfolioAccounts = this.clientPortfolioAccounts;
    // Ensure client-number binding has a value on the dialog scope
    tempScope.clientNumber = this.clientNumber;

    this.$mdDialog
      .show({
        template: '<md-dialog flex="70"><add-restriction-modal client-number="clientNumber" client="client" restriction="restriction" dropdown-options="dropdownOptions" client-portfolio-accounts="clientPortfolioAccounts"></add-restriction-modal></md-dialog>',
        scope: tempScope,
        clickOutsideToClose: true,
        escapeToClose: true
      })
      .then((result) => {
        if (result) {
          this.restrictionSaved(result);
        }
      })
      .catch((error) => {
        console.error('Error opening restriction modal:', error);
      });
  }

  // Edit restriction modal
  public editRestriction(restriction: any): void {
    if (this.isViewOnly) return;
    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.client = this.client;
    tempScope.restriction = restriction;
    tempScope.dropdownOptions = this.dropdownOptions;
    tempScope.clientPortfolioAccounts = this.clientPortfolioAccounts;
    // Ensure client-number binding has a value on the dialog scope
    tempScope.clientNumber = this.clientNumber;

    this.$mdDialog
      .show({
        template: '<md-dialog flex="70"><add-restriction-modal client-number="clientNumber" client="client" restriction="restriction" dropdown-options="dropdownOptions" client-portfolio-accounts="clientPortfolioAccounts"></add-restriction-modal></add-restriction-modal></md-dialog>',
        scope: tempScope,
        clickOutsideToClose: true,
        escapeToClose: true
      })
      .then((result) => {
        if (result) {
          this.restrictionSaved(result);
        }
      })
      .catch((error) => {
        console.error('Error opening restriction modal:', error);
      });
  }

  // Delete selected restrictions
  public deleteSelectedRestrictions(itemIds: any[]): angular.IPromise<void> | void {
    if (!itemIds || itemIds.length === 0) {
      return;
    }
    this.loadingRestrictions = true;
    return this.clientCRMRepo
      .deleteRestrictions(itemIds)
      .then(() => {
        this.loadRestrictions();
        this.loadingRestrictions = false;
      })
      .catch((error) => {
        console.error('Error deleting restrictions:', error);
        this.loadingRestrictions = false;
        throw error;
      });
  }

    // New management fee modal
    public newManagementFee(): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.managementFee = {
            portfolioNumber: '',
            accountNumber: '',
            bank: undefined,
            currency: undefined,
            dateWhichStartedToBeManaged: undefined,
            dateOnWhichEnded: undefined,
            managementFeeType: undefined,
            comments: '',
            startDateFeeType: '',
            endDateFeeType: '',
            amendmentToManagementFee: '',
            setupFee: undefined
        };
    
    // Pass dropdown options to the modal
    tempScope.dropdownOptions = this.dropdownOptions;
    // Ensure client-number binding has a value on the dialog scope
    tempScope.clientNumber = this.clientNumber;

    this.$mdDialog
      .show({
        template: '<md-dialog flex="70"><add-management-fee-modal client="client" management-fee="managementFee" dropdown-options="dropdownOptions" client-number="clientNumber"></add-management-fee-modal></md-dialog>',
        scope: tempScope,
        clickOutsideToClose: true,
        escapeToClose: true
      })
      .then((result) => {
        if (result) {
          this.managementFeeSaved(result);
        }
      })
      .catch((error) => {
        console.error('Error opening management fee modal:', error);
      });
  }

  // Edit management fee modal
  public editManagementFee(managementFee: any): void {
    if (this.isViewOnly) return;
    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.client = this.client;
    tempScope.managementFee = managementFee;
    tempScope.dropdownOptions = this.dropdownOptions;
    // Ensure client-number binding has a value on the dialog scope
    tempScope.clientNumber = this.clientNumber;

    this.$mdDialog
      .show({
        template: '<md-dialog flex="70"><add-management-fee-modal client="client" management-fee="managementFee" dropdown-options="dropdownOptions" client-number="clientNumber"></add-management-fee-modal></md-dialog>',
        scope: tempScope,
        clickOutsideToClose: true,
        escapeToClose: true
      })
      .then((result) => {
        if (result) {
          this.managementFeeSaved(result);
        }
      })
      .catch((error) => {
        console.error('Error opening management fee modal:', error);
      });
  }

  // Delete selected management fees
  public deleteSelectedManagementFees(itemIds: any[]): angular.IPromise<void> | void {
    if (!itemIds || itemIds.length === 0) {
      return;
    }
    this.loadingManagementFees = true;
    return this.clientCRMRepo
      .deleteManagementFees(itemIds)
      .then(() => {
        this.loadManagementFees();
        this.loadingManagementFees = false;
      })
      .catch((error) => {
        console.error('Error deleting management fees:', error);
        this.loadingManagementFees = false;
        throw error;
      });
  }

  // Save restriction
  private restrictionSaved(result: any): void {
    if (result && this.client && this.client.id) {
      this.loadingRestrictions = true;
      this.clientCRMRepo.saveRestriction(this.client.id, result)
        .then((response: any) => {
          // Reload the restrictions from the backend
          this.loadRestrictions();
          this.loadingRestrictions = false;
        })
        .catch((error) => {
          console.error('Error saving restriction:', error);
          this.loadingRestrictions = false;
        });
    }
  }

  // Save management fee
  private managementFeeSaved(result: any): void {
    if (result && this.client && this.client.id) {
      this.loadingManagementFees = true;
      this.clientCRMRepo.saveManagementFee(this.client.id, result)
        .then((response: any) => {
          // Reload the management fees from the backend
          this.loadManagementFees();
          this.loadingManagementFees = false;
        })
        .catch((error) => {
          console.error('Error saving management fee:', error);
          this.loadingManagementFees = false;
        });
    }
  }
}

export const ClientDetailsPortfolioComponent = {
    template: require('./clientdetails.portfolio.html'),
    style: require('./clientdetails.portfolio.scss'),
    controller: ClientDetailsPortfolioController,
    controllerAs: '$ctrl',
    bindings: {
        client: '<',
        clientNumber: '<',
        isViewOnly: '<'
    }
};



// WEBPACK FOOTER //
// ./src/app/clients/clients.details/portfolio/clientdetails.portfolio.ts